import { endOfWeek, format, getWeek, isValid, startOfWeek } from 'date-fns';
import React from 'react';
import { CHART_COLOR_SCHEME } from '../../modules/dashboard/components/attendance/UserAttendanceChart';

// Tooltip data interface
interface TooltipData {
  x: number;
  y: number;
  data: Array<{
    key: string;
    y: number;
    color: string;
    userId?: number; // Adding userId for color lookup
  }>;
  periodType?: 'day' | 'week' | 'month' | 'year'; // Added period type for date formatting
}

// Series data interface for tooltip
interface SeriesDataItem {
  key: string;
  value: number;
  color: string;
}

// User color interface
interface UserColor {
  key: number;
  color: string;
}

interface CustomTooltipContentAttendanceChartProps {
  data: TooltipData | null;
  color: string;
  userColors?: UserColor[]; // User colors array
  periodType?: 'day' | 'week' | 'month' | 'year'; // Period type prop
}

export const CustomTooltipContentAttendanceChart: React.FC<CustomTooltipContentAttendanceChartProps> = ({
  data,
  color,
  userColors,
  periodType = 'day', // Default to day if not specified
}) => {
  if (!data) return null;

  // Get period type from data or use the prop
  const currentPeriodType = data.periodType || periodType;

  // Check data validity
  try {
    const date = new Date(data.x);
    let dateHeader = '';

    // Verify that the date is valid
    if (isValid(date)) {
      // Format date based on period type
      switch (currentPeriodType) {
        case 'day':
          // For daily view: DD.MM.YYYY HH:mm
          dateHeader = format(date, 'dd.MM.yyyy HH:mm');
          break;

        case 'week':
          // For weekly view: Week XX, YYYY (DD MMM - DD MMM)
          const weekNum = getWeek(date, { weekStartsOn: 1 });
          const weekStart = startOfWeek(date, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
          dateHeader = `Week ${weekNum}, ${format(date, 'yyyy')} (${format(weekStart, 'dd MMM')} - ${format(weekEnd, 'dd MMM')})`;
          break;

        case 'month':
          // For monthly view: MMMM YYYY
          dateHeader = format(date, 'MMMM yyyy');
          break;

        case 'year':
          // For yearly view: YYYY
          dateHeader = format(date, 'yyyy');
          break;

        default:
          // Fallback to standard format
          dateHeader = format(date, 'dd.MM.yyyy');
      }
    } else {
      // Fallback if date is invalid
      dateHeader = 'Unknown date';
    }

    // Get the series data with improved error handling
    const seriesData: SeriesDataItem[] = data.data
      .filter((item) => item && typeof item === 'object') // Filter invalid data
      .map((item) => {
        // Find user color same as in UserAttendanceChart
        let seriesColor = item.color;

        // If userId and userColors array exist, look up the color by userId
        if (item.userId && userColors && userColors.length > 0) {
          const userColor = userColors.find((uc) => uc.key === item.userId);
          if (userColor) {
            seriesColor = userColor.color;
          }
        }

        // If color not found, use provided color or default
        if (!seriesColor) {
          seriesColor = color || '#888888';
        }

        // Ensure value is always a number
        const value = typeof item.y === 'number' ? item.y : 0;

        return {
          key: item.key || 'Unknown',
          value: Math.round(value * 10) / 10, // Round to one decimal place
          color: seriesColor,
        };
      })
      // Sort by user name for better readability
      .sort((a, b) => a.key.localeCompare(b.key));

    // If no data to display, show a message
    if (seriesData.length === 0) {
      return (
        <div className="p-3 rounded-md shadow-lg bg-card border">
          <div className="text-sm font-semibold text-gray-800 mb-2 border-b pb-1">{dateHeader}</div>
          <div className="text-sm text-gray-500">No data available</div>
        </div>
      );
    }

    return (
      <div className="p-3 rounded-md shadow-lg bg-card border">
        <div className="text-sm font-semibold text-gray-800 mb-2 border-b pb-1">{dateHeader}</div>
        <div className="space-y-2">
          {seriesData.map((series, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center">
                {/* Larger color indicator for better visibility */}
                <div className="w-4 h-4 mr-2 rounded-sm" style={{ backgroundColor: series.color }} />
                <span className="text-sm text-gray-700 font-medium">{series.key}:</span>
              </div>
              <span className="text-sm font-bold ml-3">{series.value} h</span>
            </div>
          ))}
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error in CustomTooltipContentAttendanceChart:', error);
    // Show simplified tooltip in case of error
    return (
      <div className="p-3 rounded-md shadow-lg bg-card border">
        <div className="text-sm font-medium text-gray-800">Error displaying data</div>
      </div>
    );
  }
};
